"use client";

import { faLocationDot } from "@fortawesome/pro-solid-svg-icons";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Icon } from "@/components/ui/Icon";
import { BRANCH_COOKIE } from "@/lib/tenant/branch-cookie";

export interface SelectorBranch {
	id: string;
	name: string;
	address: string | null;
	lat: number | null;
	lng: number | null;
}

function setBranchCookie(id: string) {
	document.cookie = `${BRANCH_COOKIE}=${id}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
	window.location.reload();
}

/**
 * Where the shopper turns location back on. A web page cannot open the
 * browser's or the phone's settings (iOS refuses the settings URL schemes and
 * Chrome refuses `chrome://` and Android settings intents), so the message names
 * the path and the button retries; read on click only, never during render.
 */
function deniedHelpKey(): "geoDeniedIos" | "geoDeniedAndroid" | "geoDeniedDesktop" {
	const ua = navigator.userAgent;
	if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1))
		return "geoDeniedIos";
	if (/Android/.test(ua)) return "geoDeniedAndroid";
	return "geoDeniedDesktop";
}

function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number) {
	const R = 6371;
	const toRad = (d: number) => (d * Math.PI) / 180;
	const dLat = toRad(bLat - aLat);
	const dLng = toRad(bLng - aLng);
	const h =
		Math.sin(dLat / 2) ** 2 +
		Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) ** 2;
	return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Branch picker for the storefront. Shows a compact "current branch · change"
 * chip, and (in `entry` mode, before a branch is chosen) a blocking modal that
 * suggests the nearest branch by geolocation or lets the shopper pick manually.
 *
 * ⚠️ The modal is a portalled `Dialog`, never an inline `fixed inset-0` block.
 * This component renders INSIDE `StoreHeader`, whose `backdrop-blur` makes the
 * header the containing block for `position: fixed`, so an inline overlay
 * resolved `inset-0` to the header's ~64px strip, and the header's own `z-30`
 * stacking context kept the modal's `z-50` from ever rising above it. Measured
 * symptom: the chooser (and its scrim) squashed into the top of the page, half
 * visible, blocking a first-time shopper on every multi-branch market. The
 * portal escapes the header entirely, like every other popup in `ui/`.
 */
export function BranchSelector({
	branches,
	currentId,
	currentName,
	mustChoose,
	nearestDefault,
}: {
	branches: SelectorBranch[];
	currentId: string | null;
	currentName: string | null;
	mustChoose: boolean;
	nearestDefault: boolean;
}) {
	const t = useTranslations("store.branchSelect");
	const [open, setOpen] = useState(mustChoose);
	const [locating, setLocating] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// A denial or a failed fix can clear on a second ask, so the location button
	// turns into the retry rather than adding a second one.
	const [canRetry, setCanRetry] = useState(false);

	// Not `useMyLocation`: the `use` prefix makes tooling (and React's own hook
	// lint rules) treat an ordinary click handler as a hook, and hooks may not
	// read browser globals during render — this only ever runs on click.
	function locateNearestBranch() {
		if (!navigator.geolocation) {
			setError(t("noGeo"));
			return;
		}
		setLocating(true);
		setError(null);
		navigator.geolocation.getCurrentPosition(
			(pos) => {
				const { latitude, longitude } = pos.coords;
				let best: SelectorBranch | null = null;
				let bestD = Infinity;
				for (const b of branches) {
					if (b.lat == null || b.lng == null) continue;
					const d = haversineKm(latitude, longitude, b.lat, b.lng);
					if (d < bestD) {
						bestD = d;
						best = b;
					}
				}
				if (best) {
					setBranchCookie(best.id);
				} else {
					setLocating(false);
					setError(t("noNearby"));
				}
			},
			(err) => {
				setLocating(false);
				setError(t(err.code === err.PERMISSION_DENIED ? deniedHelpKey() : "geoFailed"));
				setCanRetry(true);
			},
			// Branch choice needs city-block precision, so a recent cached fix is
			// good enough, and a cold GPS start indoors can take well past 8s.
			{ enableHighAccuracy: false, timeout: 20_000, maximumAge: 5 * 60_000 },
		);
	}

	return (
		<>
			{/* Compact chip */}
			<button
				type="button"
				onClick={() => setOpen(true)}
				className="inline-flex h-9 min-w-0 shrink items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 text-sm text-text transition hover:bg-surface-2"
			>
				<Icon icon={faLocationDot} size={15} />
				{/* The branch name is merchant data of any length, and this chip sits
				    between the logo and the action cluster: it truncates so a long one
				    pushes nothing off the bar. */}
				<span className="truncate font-medium">{currentName ?? t("chooseBranch")}</span>
				<span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">
					{t("change")}
				</span>
			</button>

			<Dialog
				open={open}
				// `mustChoose` is a blocking chooser: no scrim tap, no Esc, no X.
				onOpenChange={(next) => (next || !mustChoose) && setOpen(next)}
				disablePointerDismissal={mustChoose}
			>
				<DialogContent
					showCloseButton={false}
					className="max-w-md rounded-2xl border border-border bg-surface p-5 text-base text-text shadow-xl sm:max-w-md"
				>
					<div>
						<DialogTitle className="mb-1 font-sans text-lg font-bold">{t("title")}</DialogTitle>
						<DialogDescription className="mb-4 text-sm text-muted-foreground">
							{t("subtitle")}
						</DialogDescription>

						{nearestDefault && (
							<button
								type="button"
								onClick={locateNearestBranch}
								disabled={locating}
								className="btn btn-primary mb-3 w-full"
							>
								{locating ? t("locating") : canRetry ? t("retryLocation") : t("useLocation")}
							</button>
						)}
						{error && <p className="mb-3 text-sm text-danger">{error}</p>}

						{/* A branch name and address are merchant text of any length, and a
						    row that cannot break one sets a min-content floor the dialog
						    inherits: past the width of a row the popup stops shrinking,
						    slides off centre and takes its right edge (and the choice on
						    it) out of reach. `wrap-anywhere` breaks mid-word, which is
						    what keeps the floor at one character. */}
						<div className="flex max-h-72 min-w-0 flex-col gap-2 overflow-y-auto">
							{branches.map((b) => (
								<button
									key={b.id}
									type="button"
									onClick={() => setBranchCookie(b.id)}
									className={`flex w-full min-w-0 flex-col items-start rounded-lg border p-3 text-start wrap-anywhere transition hover:border-primary ${
										b.id === currentId ? "border-primary bg-primary/5" : "border-border"
									}`}
								>
									<span className="font-medium">{b.name}</span>
									{b.address && <span className="text-xs text-muted-foreground">{b.address}</span>}
								</button>
							))}
						</div>

						{!mustChoose && (
							<button
								type="button"
								onClick={() => setOpen(false)}
								className="btn btn-outline mt-4 w-full"
							>
								{t("close")}
							</button>
						)}
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
